import React, { useEffect, useState, useCallback } from "react"
import { Box, Typography } from "@mui/material"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import { useLocalization } from "../../../providers/LocalizationProvider"
import { voiceiiTranslation } from "../../../utils"

export const formatVisitDate = (date) => {
    const language = localStorage.getItem("language") || "en"
    const voiceiiObjectData = JSON.parse(localStorage.getItem("voiceiiObjectData") || "{}")
    const translate = (text) => voiceiiTranslation(text, language, voiceiiObjectData)

    const days = [
        translate("Sunday"),
        translate("Monday"),
        translate("Tuesday"),
        translate("Wednesday"),
        translate("Thursday"),
        translate("Friday"),
        translate("Saturday"),
    ]
    const months = [
        translate("Jan"),
        translate("Feb"),
        translate("Mar"),
        translate("Apr"),
        translate("May"),
        translate("Jun"),
        translate("Jul"),
        translate("Aug"),
        translate("Sep"),
        translate("Oct"),
        translate("Nov"),
        translate("Dec"),
    ]

    const d = new Date(date)
    return `${days[d.getDay()]}, ${months[d.getMonth()]} ${String(d.getDate()).padStart(
        2,
        "0",
    )}, ${d.getFullYear()} at ${String(d.getHours()).padStart(
        2,
        "0",
    )}:${String(d.getMinutes()).padStart(2, "0")}`
}

const RepeatVisitsScheduler = ({
    visitDate, // "YYYY-MM-DD"
    visitEndDate, // "YYYY-MM-DD" | undefined
    visitTime, // "HH:mm"
    repeatType, // "none" | "daily" | "weekly" | "daysInterval"
    weekData = [], // [1..7]
    intervalDays = 2,
    totalDuration = 60,
    taskCount = 0,
}) => {
    const [visits, setVisits] = useState([])
    const { voiceiiTags } = useLocalization()

    const calculateVisits = useCallback(() => {
        if (!visitDate || !visitTime) {
            setVisits([])
            return
        }

        const results = []
        const start = new Date(visitDate)
        const [h, m] = visitTime.split(":").map(Number)
        start.setHours(h, m, 0, 0)

        const end = visitEndDate ? new Date(visitEndDate) : null
        if (end) end.setHours(23, 59, 59, 999)

        const maxDate = end || new Date(start.getTime() + 90 * 86400000)

        if (repeatType === "none") {
            results.push(new Date(start))
        }

        if (repeatType === "daily") {
            let current = new Date(start)
            while (current <= maxDate) {
                results.push(new Date(current))
                current.setDate(current.getDate() + 1)
            }
        }

        if (repeatType === "weekly") {
            let current = new Date(start)
            const days = weekData.length ? weekData : [current.getDay()]

            while (current <= maxDate) {
                if (days.includes(current.getDay())) {
                    results.push(new Date(current))
                }
                current.setDate(current.getDate() + 1)
            }
        }

        if (repeatType === "every_n_days") {
            let current = new Date(start)
            while (current <= maxDate) {
                results.push(new Date(current))
                current.setDate(current.getDate() + intervalDays)
            }
        }

        setVisits(results)
    }, [visitDate, visitTime, repeatType, weekData, intervalDays, visitEndDate])

    useEffect(() => {
        calculateVisits()
    }, [calculateVisits])

    return (
        <Box className="e-repeat-visits-wrapper ps-3">
            {/* Duration */}
            <Box className="e-duration-display">
                <AccessTimeIcon />
                <Typography>
                    {voiceiiTags("Duration")}: {totalDuration} {voiceiiTags("minutes")}
                    {taskCount > 0 ? (
                        <span style={{ color: "#486684", fontSize: "0.875rem", marginLeft: 4 }}>
                            ({voiceiiTags("Calculated from")} {taskCount} {voiceiiTags("tasks")})
                        </span>
                    ) : null}
                </Typography>
            </Box>

            {/* Visits to be Created */}
            <Box className="e-visits-to-be-created-card">
                <Box className="e-section-header">
                    <CalendarTodayIcon className="e-section-header-icon" />
                    <Typography className="e-section-header-title">
                        {voiceiiTags("Visits to be Created")}
                    </Typography>
                </Box>

                <Box className="e-visits-list">
                    {visits.length === 0 ? (
                        <Typography className="e-no-visits-message">
                            {voiceiiTags("No visits scheduled yet")}
                        </Typography>
                    ) : (
                        visits.map((visit, idx) => (
                            <Box key={idx} className="e-visit-item">
                                <CalendarTodayIcon className="e-visit-item-icon" />
                                <Typography className="e-visit-item-text">
                                    {formatVisitDate(visit)}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default RepeatVisitsScheduler
